<script setup lang="ts">
import Popper from 'vue3-popper'
import Debug from 'debug'

const emit = defineEmits(['close'])

const debug = Debug('app:component:PopperWrapper')

const BODY_DISABLE_INTERACTION_CLASS = 'disable-interaction'
const popper = ref<InstanceType<typeof Popper>>()
const show = ref(false)

const attrs = useAttrs()
const key = attrs['data-popper'] || 'none'

const close = () => {
  show.value = false
  document.body.classList.remove(BODY_DISABLE_INTERACTION_CLASS)
  emit('close')
}

watch(show, () => {
  if (show.value) document.body.classList.add(BODY_DISABLE_INTERACTION_CLASS)
  else document.body.classList.remove(BODY_DISABLE_INTERACTION_CLASS)
  debug(`PopperWrapper key="${key}" show changed = ${show.value}`)
})

const onPopperMounted = () => {
  if (!popper.value) return

  debug(`PopperWrapper key="${key}" mounted`)
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'data-show') {
        if (!popper.value) return
        const currentValue = popper.value.$el.getAttribute('data-show')
        show.value = currentValue === 'show'
      }
    })
  })

  observer.observe(popper.value.$el, { attributes: true })
}
</script>

<template>
  <ClientOnly>
    <Popper
      ref="popper"
      v-bind="$attrs"
      open-delay="200"
      close-delay="100"
      :show="show"
      arrow
      @vue:mounted="onPopperMounted"
    >
      <slot :is-showed="show" />
      <template #content="props">
        <slot name="content" v-bind="props" :close="close" />
      </template>
    </Popper>
    <template #fallback>
      <!-- this will be rendered on server side -->
      <slot :is-showed="show" />
    </template>
  </ClientOnly>
</template>

<style scoped>
:deep(.popper) {
  pointer-events: auto;
  cursor: default;
}

:deep(.popper #arrow::before) {
  @apply !bg-mcd-onboardingTip;
}

:deep(.popper:hover),
:deep(.popper:hover > #arrow::before) {
  @apply !bg-mcd-onboardingTip;
}
</style>
